import { useState } from 'react';

import { StoreItemSearch, useListCategories } from '@givebacks/api/store';
import {
  ActionIcon,
  Affix,
  ButtonMantine,
  FullWidthButtonGroup,
  Modal,
  ModalProps,
  Select,
  Stack,
  Text,
} from 'ui';
import { IconFilter } from 'ui/Icons';

import { useStoreSearchContext } from '../../../../pages/Stores/Store/StoreSearchContext';
import classes from './FilterProducts.module.css';

interface FilterModalProps extends ModalProps {
  causeId: string;
}

const FilterProductsIcon = ({ onClick }: { onClick: () => void }) => {
  return (
    <ActionIcon variant="transparent" c="gray.8" onClick={onClick}>
      <IconFilter />
    </ActionIcon>
  );
};

const FilterProductsModal = ({ opened, onClose, causeId }: FilterModalProps) => {
  const { data: categories } = useListCategories({
    causeId,
    queryOptions: {
      searches: {
        hidden: { value: false },
      },
    },
    select: (data) =>
      data?.categories.map((category) => ({ label: category.name, value: category.uuid })),
  });
  //const { data: categoriesInUse } = useCategoriesInUse({ causeId });
  const [sort, setSort] = useState<{
    id: string | null;
    sort: StoreItemSearch['order'] | undefined;
  }>();
  const [categorySearch, setCategorySearch] = useState<StoreItemSearch['searches']>();
  const ctx = useStoreSearchContext();

  // id is passed in as a unique string value to set the Select component value
  const handleSort = (id: string | null) => {
    switch (id) {
      case 'asc':
        setSort({ id, sort: { field: 'price', direction: 'asc' } });
        break;
      case 'desc':
        setSort({ id, sort: { field: 'price', direction: 'desc' } });
        break;
      case 'default':
        setSort({ id, sort: { field: 'order', direction: 'asc' } });
        break;
      default:
        if (!id) {
          setSort(undefined);
        }
    }
  };

  const handleCategorySearch = (categoryId: string | null) => {
    if (categoryId) {
      setCategorySearch({ 'categories.uuid': { value: categoryId } });
    }
  };

  const handleApplyFilters = () => {
    ctx.setStoreSearchObject({
      searches: { ...ctx.storeSearchObject?.searches, ...categorySearch },
      order: sort?.sort,
    });
    onClose();
  };

  const handleResetFilters = () => {
    ctx.setStoreSearchObject({});
    setSort({ id: null, sort: undefined });
    setCategorySearch(undefined);
  };

  const sortOptions = [
    { label: 'Default', value: 'default' },
    { label: 'Price: Low-High', value: 'asc' },
    { label: 'Price: High-Low', value: 'desc' },
  ];

  return (
    <Modal opened={opened} onClose={onClose} title="Filter Products">
      <Stack gap="lg" h={272}>
        <Select
          data={sortOptions}
          leftSection={
            // The styles below are to fit the text and align text with value
            <Text fz="sm" c="gray">
              Sort by
            </Text>
          }
          clearable
          classNames={{ section: classes.sort_filter_section, input: classes.sort_input }}
          value={sort?.id}
          onChange={(value) => handleSort(value)}
        />
        <Select
          data={categories}
          leftSection={
            <Text fz="sm" c="gray">
              Category
            </Text>
          }
          clearable
          classNames={{ section: classes.category_filter_section, input: classes.category_input }}
          value={categorySearch ? (categorySearch?.['categories.uuid']?.value as string) : null}
          onChange={(value) => handleCategorySearch(value)}
        />
      </Stack>
      <Affix
        w="100%"
        mb={-24}
        pb="lg"
        withinPortal={false}
        bg="linear-gradient(to bottom, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 1) 50%)"
        style={{ position: 'sticky' }}
      >
        <FullWidthButtonGroup>
          {(sort?.id || categorySearch) && (
            <ButtonMantine variant="outline" onClick={handleResetFilters}>
              Reset Filters
            </ButtonMantine>
          )}
          <ButtonMantine onClick={handleApplyFilters}>Apply Filters</ButtonMantine>
        </FullWidthButtonGroup>
      </Affix>
    </Modal>
  );
};

const FilterProducts = ({ causeId }: { causeId: string }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  return (
    <>
      <FilterProductsIcon onClick={() => setIsModalOpen(true)} />
      <FilterProductsModal
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        causeId={causeId}
      />
    </>
  );
};

export { FilterProducts };
